import {
    LoginRequest,
    LoginResponse,
    SetPasswordRequest,
    SetPasswordResponse,
    ForgotPasswordRequest,
    ForgotPasswordResponse,
    ResetPasswordRequest,
    ResetPasswordResponse,
    SignupRequest,
    SignupResponse,
    StripeSessionResponse,
    AccountInfoResponse,
    UpdateUsernameRequest,
    UpdateUsernameResponse,
    ChangePasswordRequest,
    ChangePasswordResponse,
    CancelSubscriptionResponse,
    CancelSubscriptionRequest,
    ReactivateSubscriptionResponse,
    PaymentPortalResponse,
    AttemptSubscriptionCreationRequest,
    AttemptSubscriptionCreationResponse,
    AttemptSubscriptionCreationExistingUserRequest,
    CompleteSignupRequest,
    CompleteSignupResponse,
    AcceptInviteRequest,
    AcceptInviteResponse,
} from '../types/auth';

// Configure your API base URL here - adjust the subdomain based on your environment
const API_BASE_URL = import.meta.env.VITE_REACT_APP_API_URL;

export const authApi = {
    login: async (credentials: LoginRequest): Promise<LoginResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', // Include cookies for CORS
                body: JSON.stringify(credentials),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Login failed',
                };
            }

            return data;
        } catch (error) {
            console.error('Login error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    setPassword: async (passwordData: SetPasswordRequest): Promise<SetPasswordResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/setPassword`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', // Include cookies for CORS
                body: JSON.stringify(passwordData),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to set password',
                };
            }

            return data;
        } catch (error) {
            console.error('Set password error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    forgotPassword: async (request: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/forgotPassword`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', // Include cookies for CORS
                body: JSON.stringify(request),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to send password reset email',
                };
            }

            return data;
        } catch (error) {
            console.error('Forgot password error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    resetPassword: async (request: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/resetPassword`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', // Include cookies for CORS
                body: JSON.stringify(request),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to reset password',
                };
            }

            return data;
        } catch (error) {
            console.error('Reset password error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    signup: async (signupData: SignupRequest): Promise<SignupResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/signup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', // Include cookies for CORS
                body: JSON.stringify(signupData),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Signup failed',
                };
            }

            return {
                status: 'success',
                message: data.message || 'Account created successfully',
                playerId: data.playerId,
            };
        } catch (error) {
            console.error('Signup error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    getStripeSession: async (): Promise<StripeSessionResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/create_checkout_session`, {
                method: 'POST',
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to get stripe session',
                };
            }

            return {
                status: 'success',
                message: data.message || 'Stripe session created successfully',
                clientSecret: data.checkoutSessionClientSecret,
            };
        } catch (error) {
            console.error('Get stripe session error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    createPaymentIntent: async (
        email: string,
        userName: string,
        recaptchaToken: string
    ): Promise<any> => {
        try {
            const response = await fetch(`${API_BASE_URL}/create_payment_intent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ email, userName, recaptchaToken }),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    errorCode: data.errorCode || 'UNKNOWN_ERROR',
                    message: data.message || 'Failed to create payment intent',
                };
            }

            return {
                status: 'success',
                data: data,
            };
        } catch (error) {
            console.error('Create payment intent error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    confirmPayment: async (
        paymentIntentId: string,
        userName: string,
        password: string
    ): Promise<any> => {
        try {
            const response = await fetch(`${API_BASE_URL}/confirm_payment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ paymentIntentId, userName, password }),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to confirm payment',
                };
            }

            return {
                status: 'success',
                data: data,
            };
        } catch (error) {
            console.error('Confirm payment error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    getAccountInfo: async (): Promise<AccountInfoResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/get_account_info`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to get account information',
                };
            }

            return {
                status: 'success',
                account: data.data,
            };
        } catch (error) {
            console.error('Get account info error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    updateUsername: async (request: UpdateUsernameRequest): Promise<UpdateUsernameResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/update_player`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(request),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to update username',
                };
            }

            return {
                status: 'success',
                message: data.message || 'Username updated successfully',
            };
        } catch (error) {
            console.error('Update username error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    changePassword: async (request: ChangePasswordRequest): Promise<ChangePasswordResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/change_password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(request),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to change password',
                };
            }

            return {
                status: 'success',
                message: data.message || 'Password changed successfully',
            };
        } catch (error) {
            console.error('Change password error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    cancelSubscription: async (
        request?: CancelSubscriptionRequest
    ): Promise<CancelSubscriptionResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/cancel_subscription`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ reason: request?.reason ?? '' }),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to cancel subscription',
                };
            }

            return {
                status: 'success',
                message: data.message || 'Subscription cancelled successfully',
            };
        } catch (error) {
            console.error('Cancel subscription error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    reactivateSubscription: async (): Promise<ReactivateSubscriptionResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/reactivate_subscription`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to reactivate subscription',
                };
            }

            return {
                status: 'success',
                message: data.message || 'Subscription reactivated successfully',
            };
        } catch (error) {
            console.error('Reactivate subscription error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    getPaymentPortalUrl: async (): Promise<PaymentPortalResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/get_stripe_portal_session_url`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to get payment portal URL',
                };
            }

            return {
                status: 'success',
                portalSessionURL: data.portalSessionURL,
                message: data.message || 'Payment portal URL retrieved successfully',
            };
        } catch (error) {
            console.error('Get payment portal URL error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    attemptSubscriptionCreation: async (
        request: AttemptSubscriptionCreationRequest
    ): Promise<AttemptSubscriptionCreationResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/attempt_subscription_creation`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(request),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to create subscription',
                };
            }

            return {
                status: 'success',
                message: data.message || 'Subscription created successfully',
                playerId: data.playerId,
                hostedInvoiceUrl: data.hostedInvoiceUrl,
                subscriptionId: data.subscriptionId,
            };
        } catch (error) {
            console.error('Attempt subscription creation error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    attemptSubscriptionCreationExistingUser: async (
        request: AttemptSubscriptionCreationExistingUserRequest
    ): Promise<AttemptSubscriptionCreationResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/attempt_subscription_existing_user`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(request),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to create subscription',
                };
            }

            return {
                status: 'success',
                message: data.message || 'Subscription created successfully',
                playerId: data.playerId,
                hostedInvoiceUrl: data.hostedInvoiceUrl,
                subscriptionId: data.subscriptionId,
            };
        } catch (error) {
            console.error('Attempt subscription creation error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    completeSignup: async (request: CompleteSignupRequest): Promise<CompleteSignupResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/complete_signup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(request),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to complete signup',
                };
            }

            return {
                status: 'success',
                message: data.message || 'Signup completed successfully',
                playerId: data.playerId,
            };
        } catch (error) {
            console.error('Complete signup error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },

    acceptInvite: async (request: AcceptInviteRequest): Promise<AcceptInviteResponse> => {
        try {
            const response = await fetch(`${API_BASE_URL}/accept_invite`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(request),
            });

            const data = await response.json();

            if (!response.ok) {
                return {
                    status: 'error',
                    message: data.message || 'Failed to accept invite',
                };
            }

            return {
                status: 'success',
                message: data.message || 'Invite accepted successfully',
                playerId: data.data?.playerId,
                gameId: data.data?.gameId,
            };
        } catch (error) {
            console.error('Accept invite error:', error);
            return {
                status: 'error',
                message: 'Network error. Please try again.',
            };
        }
    },
};
